import { type MessageFormatElement, parse, TYPE } from "@formatjs/icu-messageformat-parser";
import { IntlMessageFormat } from "intl-messageformat";

const READING = { ignoreTag: true } as const;

/** The names a message gives its numbers, or what is wrong with it when it is no message. */
export function messageNames(words: string): { names: string[] } | { error: string } {
  let elements: MessageFormatElement[];
  try {
    elements = parse(words, READING);
  } catch (caught) {
    const { message, location } = caught as Error & { location?: { start: { offset: number } } };
    const at = location ? ` at character ${location.start.offset + 1}` : "";
    return { error: `${message.toLowerCase().replaceAll("_", " ")}${at}` };
  }
  const names = new Set<string>();
  const visit = (each: MessageFormatElement[]) => {
    for (const element of each) {
      if (element.type === TYPE.literal || element.type === TYPE.pound) continue;
      if (element.type === TYPE.tag) {
        visit(element.children);
        continue;
      }
      names.add(element.value);
      if (element.type === TYPE.plural || element.type === TYPE.select) {
        for (const option of Object.values(element.options)) visit(option.value);
      }
    }
  };
  visit(elements);
  return { names: [...names] };
}

const formats = new Map<string, IntlMessageFormat | null>();

/** Words as an ICU message says them with these numbers, in English: as written when they are none. */
export function worded(words: string, numbers: Record<string, number>): string {
  let format = formats.get(words);
  if (format === undefined) {
    try {
      format = new IntlMessageFormat(words, "en", undefined, READING);
    } catch {
      format = null;
    }
    formats.set(words, format);
  }
  if (!format) return words;
  try {
    return String(format.format(numbers));
  } catch {
    return words;
  }
}
